import React from "react"
import {
  PuxButton,
  PuxDialogue,
  PuxIcon,
  PuxModal,
  PuxSheet,
  PuxSimcard,
  PuxSpinner,
  PuxText,
} from "@phonero/pux-react"

import css from "./ShinySimCard.module.scss"
import { AvailableAndActiveSimCardsDocument } from "@phonero/common-ux"
import {
  ApiProductState,
  SimCard,
  SimCardType2,
} from "@phonero/common-graphql/types"
import { formatTelephoneNumber, PredefinedColors } from "@phonero/pux-core"
import { AnyFunc } from "simplytyped"
import { approved, close, editUnderline } from "@phonero/pux-icons/icons"
import classNames from "classnames"
import {
  extractMessageFromApolloError,
  dataCy,
  useAppTranslations,
  useCurrentSubscriptionId,
  Perhaps,
  useAppMutation,
  SimCardEdit,
  devPrettyJson,
  ActivateSimCardDocument
} from "@phonero/common-ux"
import { useFeatureIsOn } from "@growthbook/growthbook-react"

function ShinySimCard(
  props: {
    phoneNumber: Perhaps<string | number>
    buttonClick?: AnyFunc
    updateSimCard: (contractProductId: number, newName: string) => Promise<void>
    buttonText?: React.ReactNode
    buttonColor?: PredefinedColors
    noMargin?: boolean
    subscriptionDeactivationDate: Date | undefined
    subscriptionExpectedActivationDate: Date | undefined
  } & Pick<
    SimCard,
    | "type"
    | "pin1"
    | "puk1"
    | "embeddedSim"
    | "iccid"
    | "state"
    | "msisdn"
    | "activationDate"
    | "deactivationDate"
    | "simCardName"
    | "id"
  >
) {
  const {
    type,
    pin1,
    simCardName,
    id,
    puk1,
    embeddedSim,
    iccid,
    state: _state,
    msisdn,
    activationDate,
    deactivationDate,
    phoneNumber,
    subscriptionDeactivationDate,
    updateSimCard,
  } = props
  let { buttonText, buttonClick, buttonColor } = props

  const [activate, setActivate] = React.useState(false)
  const [showEditDialog, setShowEditDialog] = React.useState(false)
  const [updateSimCardLoading, setUpdateSimCardLoading] = React.useState(false)
  const isReadyForActivation =
    _state === ApiProductState.Startactivation && !activationDate
  const state = isReadyForActivation ? "READY_FOR_ACTIVATION" : _state
  const { t } = useAppTranslations()

  const shouldDisplayMssidn =
    type === "SUBSCRIPTION" && (!!msisdn || phoneNumber)

  const onSubmitEditDialog = (newName: string) => {
    setUpdateSimCardLoading(true)
    updateSimCard(parseInt(id as any), newName).finally(() =>
      // TODO: Fix any hack
      setUpdateSimCardLoading(false)
    )
  }
  const statusText = t("SimCard.ProductStateText", { context: state })
  const showDeveloperInfo = useFeatureIsOn("selfservice-show-developer-info")

  return (
    <>
      <PuxSimcard
        {...{ title: devPrettyJson(props, showDeveloperInfo) }}
        {...dataCy("sim-card")}
        color={type !== SimCardType2.Smartwatch ? "light" : "dark"}
        editableName={type !== SimCardType2.Subscription}
        eSim={embeddedSim}
        smartWatch={type === SimCardType2.Smartwatch}
        // Putting it here will make sure the component re-renders properly when simCardName changes
        type={!!simCardName ? t("SimCard.Name", { context: type }) : ""}
      >
        <PuxText slot="name" title={simCardName ?? ""}>
          {!!simCardName ? simCardName : t("SimCard.Name", { context: type })}
        </PuxText>
        {type !== SimCardType2.Subscription && (
          <button
            slot="nameEditButton"
            className={classNames(
              css.editButton,
              updateSimCardLoading && css.loading,
              "btn-reset"
            )}
            disabled={updateSimCardLoading}
            onClick={() => setShowEditDialog(true)}
          >
            <PuxIcon icon={editUnderline} size="extra-small"></PuxIcon>
            <PuxSpinner color="dark" />
          </button>
        )}
        <span slot="msisdn">
          {shouldDisplayMssidn &&
            formatTelephoneNumber(String(msisdn || phoneNumber)).formatted}
        </span>
        {!!statusText && statusText.trim().length > 0 ? (
          <div slot="statusText">
            <span>{statusText}</span>
          </div>
        ) : (
          <>
            {!!pin1 && <span slot="pin1">{pin1}</span>}
            {!!puk1 && <span slot="puk1">{puk1}</span>}
          </>
        )}
        {!!iccid && (
          <span slot="number">{iccid.substring(iccid.length - 12)}</span>
        )}
        <PuxText
          slot="chip"
          color={StateColor[state] || "dark"}
          style={{ textAlign: "center", fontStyle: "italic" }}
        >
          {t("SimCard.State", { context: state })}
        </PuxText>
        {!!buttonText && (
          <PuxButton
            color={buttonColor}
            // color="tertiary"
            onClick={buttonClick}
            size="small"
            style={{
              left: "calc(50% - 100px)",
              position: "fixed",
              bottom: -15,
              // minHeight: 40,
              width: 200,
              padding: 0,
              fontSize: "0.9em",
              // fontWeight: "600",
            }}
          >
            {buttonText}
          </PuxButton>
        )}
        {activate && (
          <ActivateSimDialogue
            isOpen={true}
            onClose={() => setActivate(false)}
            id={id}
          />
        )}
      </PuxSimcard>
      {showEditDialog && (
        <PuxModal isOpen onDidDismiss={() => setShowEditDialog(false)}>
          <SimCardEdit
            cardName={simCardName || ""}
            onClose={() => setShowEditDialog(false)}
            onChangeName={onSubmitEditDialog}
            title={t("SimCard.NameDialog.Title")}
            label={t("SimCard.NameDialog.Label")}
            placeholder={t("SimCard.NameDialog.Placeholder")}
            description={t("SimCard.NameDialog.Description")}
            cancel={t("SimCard.NameDialog.Cancel")}
            submit={t("SimCard.NameDialog.Submit")}
          />
        </PuxModal>
      )}
    </>
  )
}
const StateColor = {
  AVAILABLE: "dark",
  ACTIVE: "success",
  DEACTIVE: "danger",
  STARTACTIVATION: "danger",
  ERROR: "danger",
  ORDERED: undefined,
  // I: "danger",
} as const

export function ActivateSimDialogue({
  isOpen,
  id,
  onClose,
}: Pick<SimCard, "id"> &
  Pick<Parameters<typeof PuxModal>[0], "isOpen" | "onWillDismiss"> & {
    onClose: AnyFunc
  }) {
  const { subscriptionId } = useCurrentSubscriptionId()
  const [active, { loading, error, data }] = useAppMutation(
    ActivateSimCardDocument,
    {
      onError: () => null,
      refetchQueries: [
        {
          query: AvailableAndActiveSimCardsDocument,
          variables: {
            subscriptionId,
          },
        },
      ],
    }
  )
  return (
    <PuxDialogue
      title="SIM-Aktivering"
      isOpen={isOpen}
      keyboardClose
      swipeToClose
      onWillDismiss={onClose}
      footer={
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            width: "100%",
          }}
        >
          <PuxButton
            style={{ minWidth: 250, maxWidth: 350, marginBottom: 20 }}
            color="primary"
            {...(!!data
              ? {
                  children: "Lukk",
                  onClick: onClose,
                }
              : {
                  children: "Aktiver",
                  onClick: () => {
                    active({
                      variables: {
                        input: { id },
                      },
                    })
                  },
                })}
          />
        </div>
      }
    >
      <PuxSheet
        style={{
          textAlign: "center",
        }}
      >
        {loading && (
          <PuxSpinner style={{ position: "absolute", top: 0, right: 0 }} />
        )}
        {!loading && !data && !error && (
          <>
            <h4>Før aktivering!</h4>
            <p>
              Sjekk at du har mottatt det nye SIM-kortet og er klar til å gjøre
              SIM bytte.
            </p>
            <p>
              Når det nye kortet aktiveres, vil det gamle kortet samtidig bli
              deaktivert.
            </p>
          </>
        )}
        <div>
          {error && (
            <PuxText color="danger">
              <PuxIcon icon={close} />
              Det oppstod en feil: {extractMessageFromApolloError(error)}
            </PuxText>
          )}
          {data && (
            <PuxText color="success">
              <PuxIcon icon={approved} />
              Ditt SIM-kort er under aktivering.
            </PuxText>
          )}
        </div>
      </PuxSheet>
    </PuxDialogue>
  )
}

export default ShinySimCard
